import { FC, useCallback, useContext, useEffect, useState } from "react";

import { getRevolutPayOrderIdURLParam } from "@revolut/checkout";
import { AppContext } from "App";
import { useUser } from "hooks/common/useUser";
import ReactMetaPixel from "react-facebook-pixel";
import { useLocation } from "react-router-dom";
import { useGetOrderMutation, usePayOrderMutation } from "services/payments";
import { logGoogleAnalyticsEvent } from "utils/analytics";
import { logError, logMessage } from "utils/sentry";

import AppleGooglePayWidget from "./AppleGooglePayWidget";
import RevolutCardField from "./RevolutCardField";
import RevolutPay from "./RevolutPay";
import { CLIENT_PAID } from "../../../../../../constants/events";
import { useAppSelector } from "../../../../../../redux/hooks";
import { selectAnswers } from "../../../../../../redux/reducers/answers";
import { IGetOrderTokenRequest, IPayOrderRequest } from "../../../../../../types/payments";
import sendGTMEvent from "../../../../../../utils/gtm-utils";
import { createAnswersOBArray } from "../../../../../../utils/new-questionnaire";
import { NewRelic } from "../../../../../../utils/NewRelic";
import sendGaEvent from "../../../../../../utils/react-ga";
import { categories } from "../../../constants/questions";

type RevolutPaymentComponentProps = {
    setGenericPaymentFailure: (_failed: boolean) => void;
    setInsufficientFundsPaymentFailure: (_failed: boolean) => void;
    setShowPaymentSuccessModal: (_successful: boolean) => void;
    setIsLoadingOrderToken: (_loading: boolean) => void;
    setAmount: (_amount: number) => void;
    amount: number,
    documentId: string,
    handlePrev: () => void;
    handleContinue: () => void;
};

const RevolutPaymentComponent: FC<RevolutPaymentComponentProps> = ({
    setGenericPaymentFailure,
    setInsufficientFundsPaymentFailure,
    setShowPaymentSuccessModal,
    setIsLoadingOrderToken,
    setAmount,
    amount,
    documentId,
    handlePrev,
    handleContinue,
}) => {
    const { isMobile } = useContext(AppContext);
    const { answers } = useAppSelector(selectAnswers);
    const revolutEnvironment = process.env.REACT_APP_ENV === "production" ? "prod" : "sandbox";
    const { user } = useUser();
    const location = useLocation();

    const [orderToken, setOrderToken] = useState<string | null>(null);

    const [getOrder] = useGetOrderMutation();
    const [payOrder] = usePayOrderMutation();

    const handleZeroAmountPay = () => {
        if (orderToken) {
            const request: IPayOrderRequest = {
                documentId,
                body: {
                    orderToken: orderToken,
                },
            };
            payOrder(request);
            handleContinue();
        }
    };

    useEffect(() => {
        NewRelic.createCustomEvent("PaymentPageRendered", { user: user });
    }, [user]);

    const handlePaymentSuccess = useCallback((orderToken: string) => {
        logMessage("Payment successful");
        const request: IPayOrderRequest = {
            documentId,
            body: {
                orderToken: orderToken,
            },
        };
        payOrder(request);
        sendGaEvent(CLIENT_PAID);
        sendGTMEvent({
            event: "purchase",
            user_data: {
                order_id: orderToken,
                email_address: user?.info?.email,
                phone_number: user?.info?.phone,
            },
            ecommerce: {
                value: amount,
                currency: "GBP",
                items: [{ item_id: "UK_SUBMISSION_FEE" }],
            },
        });
        NewRelic.createCustomEvent("paymentSuccess", { orderToken, user: user?.id, amount });
        process.env.REACT_APP_META_PIXEL_ID && ReactMetaPixel.fbq("track", "clientPaid");
        logGoogleAnalyticsEvent("payment_successful", {
            user_id: user?.id,
        });
        setShowPaymentSuccessModal(true);
    }, [amount, documentId, payOrder, setShowPaymentSuccessModal, user]);

    const handlePaymentFailure = useCallback((orderToken: string, errorMessage: string) => {
        logError("Payment failed", {
            error: errorMessage,
            component: "RevolutPaymentComponent",
            orderToken,
        });
        NewRelic.createCustomEvent("paymentFailure", {
            orderToken,
            error: errorMessage,
            user: user?.id,
        });
        if (errorMessage === "Insufficient funds. Please top up your account or use another card") {
            setInsufficientFundsPaymentFailure(true);
        } else {
            setGenericPaymentFailure(true);
        }
    }, [setGenericPaymentFailure, setInsufficientFundsPaymentFailure, user?.id]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlOrderToken = getRevolutPayOrderIdURLParam();
        const paymentStatus = searchParams.get("paymentStatus");
        if (paymentStatus && urlOrderToken) {
            if (paymentStatus === "success") {
                handlePaymentSuccess(urlOrderToken);
            } else if (paymentStatus === "failure") {
                handlePaymentFailure(urlOrderToken, "Payment failure");
            }
        } else {
            const answersOb = createAnswersOBArray(answers, categories);
            const request: IGetOrderTokenRequest = {
                documentId,
                body: {
                    accountantReviewRequested: false,
                    answersOB: {
                        convertedAnswers: answersOb,
                        answers: answers,
                        shouldComplete: true,
                        saveDate: new Date(),
                    },
                },
            };
            getOrder(request).unwrap().then(order => {
                setOrderToken(order.orderToken);
                const amount = order.amountInPence / 100;
                setIsLoadingOrderToken(false);
                setAmount(amount);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers, documentId, getOrder, handlePaymentFailure, handlePaymentSuccess, location.search, setAmount, setIsLoadingOrderToken]);

    if (amount <= 0) {
        return (<div className="no-payment-continue-button-container">
            <button className={`no-payment-continue-button${isMobile ? "-mobile" : "-desktop"} mt60`}
                onClick={handleZeroAmountPay}>
                Continue
            </button>
        </div>);
    }

    return (
        <div>
            <div className="revolut-apple-google-pay-container-background">
                <span className="revolut-apple-google-pay-title">
                    Express Checkout
                </span>
                <div className="revolut-apple-google-pay-container">
                    <AppleGooglePayWidget amountInPence={amount * 100} handlePaymentFailure={handlePaymentFailure} handlePaymentSuccess={handlePaymentSuccess} orderToken={orderToken} revolutEnvironment={revolutEnvironment} platform="google" />
                    <AppleGooglePayWidget amountInPence={amount * 100} handlePaymentFailure={handlePaymentFailure} handlePaymentSuccess={handlePaymentSuccess} orderToken={orderToken} revolutEnvironment={revolutEnvironment} platform="apple" />
                    <RevolutPay amountInPence={amount * 100} handlePaymentFailure={handlePaymentFailure} handlePaymentSuccess={handlePaymentSuccess} orderToken={orderToken} revolutEnvironment={revolutEnvironment} />
                </div>
            </div>
            <div className={`revolut-payment-outer-container${isMobile ? "-mobile" : "-desktop"}`} data-testid="revolut-payment-component">
                <RevolutCardField amount={amount} orderToken={orderToken} user={user} isMobile={isMobile} revolutEnvironment={revolutEnvironment} handlePaymentSuccess={handlePaymentSuccess} handlePaymentFailure={handlePaymentFailure} handlePrev={handlePrev}/>
            </div>
        </div>
    );
};

export default RevolutPaymentComponent;
